import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useBreadcrumbs, useUser } from '../hooks';
import { Header, Table, Groupper, Button, Message, Toast, Label, Toolbar, Modal, Icon, Input, Field } from 'react-frontier';
import { Dropdown } from 'semantic-ui-react';
import { addCommas, getEmploymentLogTypeName, getEmploymentScheduleTypeName } from '../SiteUtil';
import EmploymentPayoutStpModal from '../components/EmploymentPayoutStpModal';
import UserAccess from '../UserAccess';
import classNames from 'classnames';
import moment from 'moment';
import API from '../API';

interface PayoutLine{
	payout_user_id: number,
	user_id: number,
	name: string,
	employment_id: number,
	employment_name: string,
	type_id: number,
	type_name: string,
	amount: number,
	paid: number,
	cancelled: number,
	date_created: number,
	user_creator_name: string,
	payments: number,
	has_account: number,
	payout_type: number,
}

interface PayoutLog{
	log_id: number,
	employment_name: string,
	salary: number,
	date_start: number,
	type_id: number,
	log_type_id: number,
}

interface AdjustmentType{
	type_id: number,
	type_name: string,
}

interface Payout{
	payout_id: number,
	date_start: number,
	date_end: number,
	date_created: number,
	deleted: number,
	user_creator_name: string,
	users: PayoutLine[],
	adjustment_types: AdjustmentType[],
}

interface UserRow{
	user_id: number,
	name: string,
	employments: string[],
	payments: number,
	amount: number,
	adjustment_amount: number,
	cancelled_amount: number,
	net: number,
	paid: boolean,
	cancelled: boolean,
	has_account: boolean,
	payout_type: number,
}

enum Modals{
	INFO = 1,
	PAID = 2,
	UNPAID = 3,
	CANCEL = 4,
	EDIT = 5,
	PAY_MANY = 6,
	ADJUSTMENT = 7,
	CANCEL_MANY = 8,
	DELETE = 9,
	ASSIGN_TYPE = 10,
}

var groupUsers = (lines: PayoutLine[]): UserRow[] =>{
	var map = new Map<number, UserRow>();
	for(var l of lines){
		if(!map.has(l.user_id)) map.set(l.user_id, {
			user_id: l.user_id,
			name: l.name,
			employments: [],
			payments: 0,
			amount: 0,
			adjustment_amount: 0,
			cancelled_amount: 0,
			net: 0,
			paid: true,
			cancelled: true,
			has_account: !!l.has_account,
			payout_type: l.payout_type,
		});
		var g = map.get(l.user_id);
		if(l.type_id){
			if(!l.cancelled) g.adjustment_amount += (l.amount || 0);
			continue;
		}
		if(l.cancelled){
			g.cancelled_amount += (l.amount || 0);
			continue;
		}
		g.employments.push(l.employment_name);
		g.payments += l.payments;
		g.amount += l.amount;
		g.cancelled = false;
		if(!l.paid) g.paid = false;
	}
	return Array.from(map.values()).map(g => ({
		...g,
		paid: g.cancelled ? false : g.paid,
		net: g.cancelled ? g.cancelled_amount : g.amount + g.adjustment_amount,
	}));
};

var EmploymentPayoutView = ()=>{
	var params = useParams<{ id: string }>();
	var navigate = useNavigate();
	var { setTitle } = useBreadcrumbs();
	var { hasAccess, user } = useUser();
	var [loadError, setLoadError] = useState<string>(null);
	var [payout, setPayout] = useState<Payout>(null);
	var [prompts, setPrompts] = useState<string[]>(null);
	var [loading, setLoading] = useState<boolean>(false);
	var [stpModal, setStpModal] = useState<boolean>(false);
	var [shownModal, setShownModal] = useState<number>(null);
	var [selectedUserId, setSelectedUserId] = useState<number>(null);
	var [editAmount, setEditAmount] = useState<string>('');
	var [manyUsers, setManyUsers] = useState<number[]>([]);
	var [manyRows, setManyRows] = useState<UserRow[]>([]);
	var [manyTitle, setManyTitle] = useState<string>('');
	var [userLogs, setUserLogs] = useState<PayoutLog[]>(null);
	var [logsError, setLogsError] = useState<string>(null);
	var [adjustmentType, setAdjustmentType] = useState<number>(null);
	var [adjustmentAmount, setAdjustmentAmount] = useState<string>('');
	var [payoutTypes, setPayoutTypes] = useState<{ type_id: number, type_name: string }[]>([]);
	var [assignType, setAssignType] = useState<number>(null);

	var load = ()=>{
		API.getEmploymentPayout(parseInt(params.id)).then(res=>{
			if(res.error) return setLoadError(res.message);
			setPayout(res.data);
		}).catch(()=> setLoadError('Hubo un error cargando el pago (LCL-1)'));
	};

	useEffect(()=>{
		setTitle('Pago #' + parseInt(params.id));
		load();
		API.getPayoutTypes().then(res=>{
			if(!res.error) setPayoutTypes(res.data);
		}).catch(()=>{});
	}, []);

	if(loadError) return <Header text='Error' subtext={loadError} />
	if(!payout) return <Header text='Cargando pago' loading/>

	var rows = groupUsers(payout.users).filter(r => !r.cancelled);
	var stpRows = rows.filter(r => r.payout_type === 1);
	var nominaRows = rows.filter(r => !!r.payout_type && r.payout_type !== 1);
	var noTypeRows = rows.filter(r => !r.payout_type);
	var stpPending = stpRows.filter(r => !r.paid && !r.cancelled);
	var nominaPending = nominaRows.filter(r => !r.paid && !r.cancelled);
	var total = rows.reduce((a, r)=> a + (r.cancelled ? 0 : r.net), 0);
	var paidTotal = rows.filter(r => r.paid && !r.cancelled).reduce((a, r)=> a + r.net, 0);
	var canPay = hasAccess(UserAccess.EMPLOYMENTS.PAY_PAYOUT);
	var canCancel = hasAccess(UserAccess.EMPLOYMENTS.CANCEL_PAYOUT);
	var canAdjust = hasAccess(UserAccess.EMPLOYMENTS.CREATE_PAYOUT);
	var canDelete = !!user?.superadmin && !payout.deleted;
	var canAssignType = hasAccess(UserAccess.USERS.EDIT_USERS);
	var selectedRow = rows.find(r => r.user_id === selectedUserId) || null;
	var selectedAdjustments = payout.users.filter(l => l.user_id === selectedUserId && !!l.type_id && !l.cancelled);
	var adjustmentHeaderStyle: React.CSSProperties = {
		background: '#f9fafb',
		borderBottom: '1px solid #e8e8e8',
		padding: '0.4rem 0.8rem',
		fontSize: 12,
		fontWeight: 700,
		color: '#4a4a4a',
		textAlign: 'center',
	};

	var openModal = (r: UserRow, modal: number)=>{
		setPrompts(null);
		setSelectedUserId(r.user_id);
		setEditAmount(String(r.amount || ''));
		setShownModal(modal);
		if(modal !== Modals.INFO) return;
		setAdjustmentType(null);
		setAdjustmentAmount('');
		setUserLogs(null);
		setLogsError(null);
		API.getEmploymentPayoutUserLogs(payout.payout_id, r.user_id).then(res=>{
			if(res.error) return setLogsError(res.message);
			setUserLogs(res.data);
		}).catch(()=> setLogsError('Hubo un error cargando los registros del pago (LCL-2)'));
	};

	var runAdjustment = (fn: Promise<any>, ok: string, err: string)=>{
		setLoading(true);
		setPrompts(null);
		fn.then(res=>{
			if(res.error) return setPrompts([res.message]);
			Toast.success(ok);
			setAdjustmentType(null);
			setAdjustmentAmount('');
			setShownModal(Modals.INFO);
			load();
		}).catch(()=> setPrompts([err]))
		.finally(()=> setLoading(false));
	};

	var closeAdjustment = ()=>{
		setPrompts(null);
		setShownModal(Modals.INFO);
	};

	var openAdjustment = ()=>{
		setPrompts(null);
		setAdjustmentType(null);
		setAdjustmentAmount('');
		setShownModal(Modals.ADJUSTMENT);
	};

	var createAdjustment = ()=>{
		var amount = Math.round(parseFloat(adjustmentAmount) * 100) / 100;
		if(!adjustmentType) return setPrompts(['Selecciona un tipo de ajuste.']);
		if(Number.isNaN(amount) || amount === 0) return setPrompts(['Ingresa un monto válido.']);
		runAdjustment(
			API.createEmploymentPayoutAdjustment(payout.payout_id, selectedRow.user_id, adjustmentType, amount),
			'Ajuste guardado!',
			'Hubo un error agregando el ajuste (ADJ-1)'
		);
	};

	var deleteAdjustment = (payout_user_id: number)=>{
		runAdjustment(
			API.deleteEmploymentPayoutAdjustment(payout_user_id),
			'Ajuste eliminado!',
			'Hubo un error eliminando el ajuste (ADJ-2)'
		);
	};

	var assignPayoutType = ()=>{
		if(!assignType) return setPrompts(['Selecciona un tipo de pago.']);
		runAction(
			API.setUserPayoutType(selectedRow.user_id, assignType),
			'Tipo de pago asignado!',
			'Hubo un error asignando el tipo de pago (APT-1)'
		);
	};

	var openMany = (modal: number, title: string, list: UserRow[])=>{
		setPrompts(null);
		setManyTitle(title);
		setManyRows(list);
		setManyUsers(list.filter(r => modal !== Modals.PAY_MANY || r.net > 0).map(r => r.user_id));
		setShownModal(modal);
	};

	var toggleManyUser = (user_id: number)=>{
		setManyUsers(prev => prev.indexOf(user_id) === -1 ? [...prev, user_id] : prev.filter(u => u !== user_id));
	};

	var manySelectable = shownModal === Modals.PAY_MANY ? manyRows.filter(r => r.net > 0) : manyRows;

	var deletePayout = ()=>{
		setLoading(true);
		setPrompts(null);
		API.deleteEmploymentPayout(payout.payout_id).then(res=>{
			if(res.error) return setPrompts([res.message]);
			Toast.success('Nómina eliminada!');
			navigate('/employees/payouts');
		}).catch(()=> setPrompts(['Hubo un error eliminando la nómina (DEL-1)']))
		.finally(()=> setLoading(false));
	};

	var downloadBanorte = ()=>{
		setPrompts(null);
		API.downloadEmploymentPayoutBanorte(payout.payout_id, nominaPending.map(r => r.user_id))
			.catch(()=> Toast.error('Hubo un error descargando el archivo Banorte (BAN-1)'));
	};

	var downloadPayments = ()=>{
		setPrompts(null);
		API.downloadPaymentsReport(`Nomina ${payout.payout_id}`, null, payout.payout_id).then(res=>{
			if(res.error) return Toast.error(res.message);
		}).catch(()=> Toast.error('Hubo un error descargando los pagos (PAY-CSV-1)'));
	};

	var runAction = (fn: Promise<any>, ok: string, err: string)=>{
		setLoading(true);
		setPrompts(null);
		fn.then(res=>{
			if(res.error) return setPrompts([res.message]);
			Toast.success(ok);
			setShownModal(null);
			setSelectedUserId(null);
			load();
		}).catch(()=> setPrompts([err]))
		.finally(()=> setLoading(false));
	};

	var renderRow = (r: UserRow)=> (
		<Table.Row
			key={r.user_id}
			collapsingIndexes={[1, 2, 3, 4]}
			centeredIndexes={[1, 2, 3, 4]}
			data={[
				r.name,
				r.payments,
				'$' + addCommas(r.net || 0),
				r.cancelled
					? <Label color='red'>Cancelado</Label>
					: r.paid
						? <Label color='green'>Pagado</Label>
						: <Label color='orange'>Pendiente</Label>,
				<Dropdown icon={(
					<div className="fr label" style={{ padding: '3px 10px' }}>
						<Icon name='ellipsis' style={{ margin: 0 }} />
					</div>
				)} className='icon'>
					<Dropdown.Menu>
						{!r.payout_type ? (
							canAssignType ? (
								<Dropdown.Item onClick={()=>{ setAssignType(null); openModal(r, Modals.ASSIGN_TYPE); }}>
									<Icon name='tag' style={{ marginRight: 3 }} /> Asignar tipo
								</Dropdown.Item>
							) : null
						) : <>
						<Dropdown.Item onClick={()=> openModal(r, Modals.INFO)}>
							<Icon name='eye' style={{ marginRight: 3 }} /> Info
						</Dropdown.Item>
						{!r.cancelled && !r.paid && canPay ? (
							<Dropdown.Item onClick={()=> openModal(r, Modals.PAID)}>
								<Icon name='money-bill' style={{ marginRight: 3 }} /> Pagar
							</Dropdown.Item>
						) : null}
						{!r.cancelled && r.paid && canPay ? (
							<Dropdown.Item onClick={()=> openModal(r, Modals.UNPAID)}>
								<Icon name='undo' style={{ marginRight: 3 }} /> Marcar no pagado
							</Dropdown.Item>
						) : null}
						{!r.cancelled && !r.paid && user?.superadmin ? (
							<Dropdown.Item onClick={()=> openModal(r, Modals.EDIT)}>
								<Icon name='pen' style={{ marginRight: 3 }} /> Editar
							</Dropdown.Item>
						) : null}
						{!r.cancelled && !r.paid && canCancel ? (
							<Dropdown.Item onClick={()=> openModal(r, Modals.CANCEL)}>
								<Icon name='close' style={{ marginRight: 3 }} /> Cancelar
							</Dropdown.Item>
						) : null}
						</>}
					</Dropdown.Menu>
				</Dropdown>
			]}
		/>
	);

	var manySelectionTable = ()=> <>
		<div style={{ textAlign: 'center' }}>
			<Button text='Seleccionar todos' color='black' onClick={()=>{
				setManyUsers(manyUsers.length === manySelectable.length ? [] : manySelectable.map(r => r.user_id));
			}} />
		</div>
		<Table
			selectable fitted
			style={{ marginTop: 15 }}
			headers={[
				<div style={{ textAlign: 'center' }}><i className='checkmark icon' style={{ marginRight: 0 }}></i></div>,
				'Empleado',
				'Pagos',
				'Monto'
			]}
			collapsingIndexes={[0, 2, 3]}
			centeredIndexes={[0, 2, 3]}
			onClick={(v: any, i: number)=> toggleManyUser(manyRows[i].user_id)}
			data={manyRows.map(r=>{
				var isSelected = manyUsers.indexOf(r.user_id) !== -1;
				return [
					<i className={classNames('large icon circle', { check: isSelected, outline: !isSelected })}></i>,
					r.name,
					r.payments,
					'$' + addCommas(r.net || 0)
				];
			})}
		/>
		<div style={{ textAlign: 'right', fontWeight: 500, marginTop: 8 }}>
			Total: ${addCommas(manyRows.filter(r => manyUsers.indexOf(r.user_id) !== -1).reduce((a, r)=> a + (r.net || 0), 0))}
		</div>
		<Message type='error' list={prompts} />
	</>;

	var payoutTable = (title: string, list: UserRow[], button?: JSX.Element)=> (
		<Table
			striped fitted
			title={title}
			button={button}
			style={{ width: 800, margin: 'auto', marginTop: 20 }}
			headers={['Empleado', 'Pagos', 'Monto', 'Estado', <Icon name='wrench' />]}
			collapsingIndexes={[1, 2, 3, 4]}
			centeredIndexes={[1, 2, 3, 4]}
		>
			{list.length !== 0 ? list.map(renderRow) : (
				<Table.Cell row className='empty' colSpan={5}>No hay pagos</Table.Cell>
			)}
		</Table>
	);

	var paid = rows.length > 0 && rows.every(r => r.paid);

	return <div>
		<Groupper title='Pago de nómina' width={600} style={{ marginTop: 20 }} fitted>
			<Toolbar>
				<Toolbar.Item text='Pagos' iconName='money-bill' onClick={downloadPayments} />
				{canPay && stpPending.length > 0 && (
					<Toolbar.Item text='Descargar STP' iconName='file-download' onClick={()=> setStpModal(true)} />
				)}
				{canPay && nominaPending.length > 0 && (
					<Toolbar.Item text='Descargar Banorte' iconName='file-download' onClick={downloadBanorte} />
				)}
				{canDelete && !paid && (
					<Toolbar.Item text='Eliminar' iconName='trash' onClick={()=>{ setPrompts(null); setShownModal(Modals.DELETE); }} />
				)}
			</Toolbar>
			<Table fitted details striped>
				<Table.Row data={['ID', payout.payout_id]} />
				<Table.Row data={['Semana', `${moment.unix(payout.date_start).format('DD/MM/YYYY')} - ${moment.unix(payout.date_end).format('DD/MM/YYYY')}`]} />
				<Table.Row data={['Creador', payout.user_creator_name]} />
				<Table.Row data={['Empleados', rows.length]} />
				<Table.Row data={['Estatus', <Label color={paid ? 'green' : 'orange'}>{paid ? 'Pagada' : 'Pendiente'}</Label>]} />
				<Table.Row data={['Pagos', payout.users.filter(l => !l.type_id && !l.cancelled).length]} />
				<Table.Row data={['Pagado', '$' + addCommas(paidTotal)]} />
				<Table.Row data={['Total', '$' + addCommas(total)]} />
			</Table>
		</Groupper>
		{payoutTable('Pagos STP', stpRows, canCancel && stpPending.length > 0 && (
			<Button text='Marcar cancelados' color='black' iconName='close' size='tiny' onClick={()=> openMany(Modals.CANCEL_MANY, 'STP', stpPending)} />
		))}
		{payoutTable('Pagos Nómina', nominaRows, (canPay || canCancel) && nominaPending.length > 0 && (
			<div style={{ display: 'flex', gap: 5 }}>
				{canPay && <Button text='Marcar pagados' color='black' iconName='money-bill' size='tiny' onClick={()=> openMany(Modals.PAY_MANY, 'Nómina', nominaPending)} />}
				{canCancel && <Button text='Marcar cancelados' color='black' iconName='close' size='tiny' onClick={()=> openMany(Modals.CANCEL_MANY, 'Nómina', nominaPending)} />}
			</div>
		))}
		{noTypeRows.length > 0 && payoutTable('Sin tipo de pago', noTypeRows)}
		<Modal open={shownModal === Modals.ASSIGN_TYPE} size='tiny' onClose={()=> setShownModal(null)}>
			<Modal.Header>Asignar tipo de pago</Modal.Header>
			<Modal.Content>
				<Field label={selectedRow ? selectedRow.name : 'Tipo de pago'}>
					<Dropdown
						fluid selection
						placeholder='Tipo de pago'
						value={assignType}
						options={payoutTypes.map(t => ({ key: t.type_id, value: t.type_id, text: t.type_name }))}
						onChange={(_, { value })=> setAssignType(value as number)}
					/>
				</Field>
				<Message type='error' list={prompts} />
			</Modal.Content>
			<Modal.Actions>
				<Button text='Cancelar' color='black' basic onClick={()=> setShownModal(null)} />
				<Button text='Asignar' color='black' loading={loading} onClick={assignPayoutType} />
			</Modal.Actions>
		</Modal>
		<EmploymentPayoutStpModal
			open={stpModal}
			payout_id={payout.payout_id}
			users={stpPending.map(r => ({ user_id: r.user_id, name: r.name, amount: r.net, has_account: r.has_account }))}
			onClose={()=> setStpModal(false)}
			onPaid={()=>{ setStpModal(false); load(); }}
		/>
		<Modal open={shownModal === Modals.INFO} size='tiny' onClose={()=> setShownModal(null)}>
			<Modal.Header>Información de pago</Modal.Header>
			{selectedRow && <Modal.Content style={{ paddingBottom: 0, maxHeight: 650, overflowY: 'auto', overflowX: 'hidden', padding: 0}}>
				{canAdjust && !selectedRow.paid && !selectedRow.cancelled && (
					<Toolbar>
						<Toolbar.Item text='Ajuste' iconName='money-bill' onClick={openAdjustment}/>
					</Toolbar>
				)}
				<Table
					striped details
					data={[
						['ID', selectedRow.user_id],
						['Nombre', selectedRow.name],
						['Empleos', selectedRow.employments.join(', ')],
						['Pagos', selectedRow.payments],
						['Subtotal', '$' + addCommas(selectedRow.amount || 0)],
						['Ajustes', '$' + addCommas(selectedRow.adjustment_amount || 0)],
						['Total', '$' + addCommas(selectedRow.net || 0)],
						['Cuenta bancaria', selectedRow.has_account
							? <><Icon name='check' color='green'/> Sí</>
							: <><Icon name='close' color='red'/> No</>],
						['Estatus', <div className={classNames('fr label', {
							green: selectedRow.paid && !selectedRow.cancelled,
							yellow: !selectedRow.paid && !selectedRow.cancelled,
							red: selectedRow.cancelled
						})}>
							{selectedRow.cancelled ? 'Cancelado' : selectedRow.paid ? 'Pagado' : 'No pagado'}
						</div>]
					]}
				/>
				{logsError ? (
					<Message type='error' text={logsError} style={{ marginTop: 20 }} />
				) : !userLogs ? (
					<Header loading text='Cargando registros...' style={{ marginTop: 20 }} />
				) : (
					<Table
						striped
						title='Registros'
						style={{ marginTop: 10 }}
						headers={['Fecha', 'Empleo', 'Tipo', 'Monto', '']}
						collapsingIndexes={[0, 2, 3, 4]}
						centeredIndexes={[0, 2, 3]}
					>
						{userLogs.length === 0 && selectedAdjustments.length === 0 ? (
							<Table.Cell row className='empty' colSpan={5}>No hay registros para este pago</Table.Cell>
						) : (
							<>
								{userLogs.map(l=>(
									<Table.Row
										key={`PAYLOG-${l.log_id}`}
										collapsingIndexes={[0, 2, 3, 4]}
										centeredIndexes={[0, 2, 3]}
										data={[
											moment.unix(l.date_start).format('DD/MM/YYYY'),
											l.employment_name,
											<Label color={l.log_type_id ? 'orange' : 'green'} style={{ width: 100 }}>
												{l.log_type_id ? getEmploymentLogTypeName(l.log_type_id) : getEmploymentScheduleTypeName(l.type_id)}
											</Label>,
											'$' + addCommas(l.salary || 0),
											''
										]}
									/>
								))}
								{selectedAdjustments.length > 0 && (
									<Table.Row key='PAYADJ-SEPARATOR'>
										<Table.Cell colSpan={5} style={{
											background: '#fafafa',
											borderTop: '2px solid #d4d4d5',
											borderBottom: '1px solid #e8e8e8',
											padding: '0.5rem 0.8rem',
											fontSize: 11,
											fontWeight: 700,
											letterSpacing: 0.8,
											textTransform: 'uppercase',
											color: '#767676',
										}}>
											Ajustes
										</Table.Cell>
									</Table.Row>
								)}
								{selectedAdjustments.length > 0 && (
									<Table.Row key='PAYADJ-HEADERS'>
										<Table.Cell collapsing style={adjustmentHeaderStyle}>Fecha</Table.Cell>
										<Table.Cell style={adjustmentHeaderStyle}>Tipo</Table.Cell>
										<Table.Cell style={adjustmentHeaderStyle}>Creado por</Table.Cell>
										<Table.Cell collapsing style={adjustmentHeaderStyle}>Monto</Table.Cell>
										<Table.Cell collapsing style={adjustmentHeaderStyle} />
									</Table.Row>
								)}
								{selectedAdjustments.map(a=>(
									<Table.Row key={`PAYADJ-${a.payout_user_id}`}>
										<Table.Cell collapsing style={{ textAlign: 'center' }}>
											{moment.unix(a.date_created).format('DD/MM/YYYY')}
										</Table.Cell>
										<Table.Cell style={{ textAlign: 'center' }}>
											<Label color={a.amount < 0 ? 'red' : 'blue'} style={{ width: 100 }}>{a.type_name}</Label>
										</Table.Cell>
										<Table.Cell style={{ textAlign: 'center' }}>{a.user_creator_name || '---'}</Table.Cell>
										<Table.Cell collapsing style={{ textAlign: 'center' }}>
											${addCommas(a.amount || 0)}
										</Table.Cell>
										<Table.Cell collapsing>
											{canAdjust && !selectedRow.paid && !selectedRow.cancelled ? (
												<Button icon basic size='tiny' iconName='trash' onClick={()=> deleteAdjustment(a.payout_user_id)} />
											) : ''}
										</Table.Cell>
									</Table.Row>
								))}
							</>
						)}
					</Table>
				)}
				<Message type='error' list={prompts} style={{ marginTop: 15 }} />
			</Modal.Content>}
			<Modal.Actions>
				<Button basic text='Cerrar' onClick={()=> setShownModal(null)} />
			</Modal.Actions>
		</Modal>
		<Modal open={shownModal === Modals.ADJUSTMENT} size='tiny' onClose={closeAdjustment}>
			<Modal.Header>Agregar ajuste</Modal.Header>
			{selectedRow && <Modal.Content>
				<Header text={selectedRow.name} subtext={`Total actual: $${addCommas(selectedRow.net || 0)}`} />
				<Field label='Tipo'>
					<Dropdown
						selection
						fluid
						placeholder='Selecciona un tipo...'
						value={adjustmentType}
						options={(payout.adjustment_types || []).map(t => ({ key: t.type_id, value: t.type_id, text: t.type_name }))}
						onChange={(ev, data)=> setAdjustmentType(data.value as number)}
					/>
				</Field>
				<Field>
					<Input
						label='Monto'
						value={adjustmentAmount}
						onChange={setAdjustmentAmount}
						inputType='number'
						placeholder='0.00'
						comment='Usa un monto negativo para descuentos.'
						style={{ width: '100%' }}
					/>
				</Field>
				<Message type='error' list={prompts} style={{ marginTop: 15 }} />
			</Modal.Content>}
			<Modal.Actions>
				<Button basic text='Cerrar' onClick={closeAdjustment} />
				<Button text='Guardar' color='black' loading={loading} disabled={loading} onClick={createAdjustment} />
			</Modal.Actions>
		</Modal>
		<Modal open={shownModal === Modals.PAID} size='tiny' onClose={()=> setShownModal(null)}>
			<Modal.Header>¿Marcar como pagado?</Modal.Header>
			{selectedRow && <Modal.Content>
				<Header text='¿Marcar como pagado?' />
				<Table details striped data={[
					['Empleado', selectedRow.name],
					['Pagos', selectedRow.payments],
					['Monto', '$' + addCommas(selectedRow.net || 0)],
				]} />
				<Message type='error' list={prompts} />
			</Modal.Content>}
			<Modal.Actions>
				<Button text='Cerrar' basic onClick={()=> setShownModal(null)} />
				<Button text='Guardar' color='black' loading={loading}
					onClick={()=> runAction(API.payEmploymentPayout(payout.payout_id, [selectedRow.user_id], true), 'Pago realizado!', 'Hubo un error realizando el pago (PAY-1)')} />
			</Modal.Actions>
		</Modal>
		<Modal open={shownModal === Modals.PAY_MANY} size='small' onClose={()=> setShownModal(null)}>
			<Modal.Header>Marcar como pagados</Modal.Header>
			<Modal.Content style={{ maxHeight: 700, overflowY: 'auto', overflowX: 'hidden' }}>
				<Header text='¿Marcar como pagados?' subtext='Selecciona los empleados que quieres marcar como pagados.' />
				{manySelectionTable()}
			</Modal.Content>
			<Modal.Actions>
				<Button text='Cerrar' basic onClick={()=> setShownModal(null)} />
				<Button text='Marcar pagados' color='black' loading={loading} disabled={manyUsers.length === 0}
					onClick={()=> runAction(API.payEmploymentPayout(payout.payout_id, manyUsers, true), 'Pagos realizados!', 'Hubo un error realizando los pagos (PAY-3)')} />
			</Modal.Actions>
		</Modal>
		<Modal open={shownModal === Modals.CANCEL_MANY} size='small' onClose={()=> setShownModal(null)}>
			<Modal.Header>Cancelar pagos {manyTitle}</Modal.Header>
			<Modal.Content style={{ maxHeight: 700, overflowY: 'auto', overflowX: 'hidden' }}>
				<Header text='¿Cancelar pagos?' subtext='Selecciona los empleados que quieres cancelar. Sus días quedan cancelados y no saldrán en nóminas futuras.' />
				{manySelectionTable()}
			</Modal.Content>
			<Modal.Actions>
				<Button text='Cerrar' basic onClick={()=> setShownModal(null)} />
				<Button text='Marcar cancelados' color='black' loading={loading} disabled={manyUsers.length === 0}
					onClick={()=> runAction(API.cancelEmploymentPayout(payout.payout_id, manyUsers), 'Pagos cancelados!', 'Hubo un error cancelando los pagos (CAN-2)')} />
			</Modal.Actions>
		</Modal>
		<Modal open={shownModal === Modals.UNPAID} size='tiny' onClose={()=> setShownModal(null)}>
			<Modal.Header>¿Marcar como no pagado?</Modal.Header>
			{selectedRow && <Modal.Content>
				<Header text='¿Marcar como no pagado?' />
				<Table details striped data={[
					['Empleado', selectedRow.name],
					['Pagos', selectedRow.payments],
					['Monto', '$' + addCommas(selectedRow.net || 0)],
				]} />
				<Message type='error' list={prompts} />
			</Modal.Content>}
			<Modal.Actions>
				<Button text='Cerrar' basic onClick={()=> setShownModal(null)} />
				<Button text='Guardar' color='black' loading={loading}
					onClick={()=> runAction(API.payEmploymentPayout(payout.payout_id, [selectedRow.user_id], false), 'Pago marcado como no pagado!', 'Hubo un error marcando el pago (PAY-2)')} />
			</Modal.Actions>
		</Modal>
		<Modal open={shownModal === Modals.EDIT} size='tiny' onClose={()=> setShownModal(null)}>
			<Modal.Header>Editar monto</Modal.Header>
			{selectedRow && <Modal.Content>
				<Table details striped data={[
					['Empleado', selectedRow.name],
					['Pagos', selectedRow.payments],
					['Monto actual', '$' + addCommas(selectedRow.amount || 0)],
				]} />
				<Input label='Nuevo monto' value={editAmount} onChange={setEditAmount} />
				<Message type='error' list={prompts} />
			</Modal.Content>}
			<Modal.Actions>
				<Button text='Cerrar' basic onClick={()=> setShownModal(null)} />
				<Button text='Guardar' color='black' loading={loading}
					onClick={()=> runAction(API.editEmploymentPayout(payout.payout_id, selectedRow.user_id, parseFloat(editAmount)), 'Monto actualizado!', 'Hubo un error editando el monto (EDT-1)')} />
			</Modal.Actions>
		</Modal>
		<Modal open={shownModal === Modals.DELETE} size='tiny' onClose={()=> setShownModal(null)}>
			<Modal.Header>¿Eliminar nómina?</Modal.Header>
			<Modal.Content>
				<Header text='¿Eliminar esta nómina?' subtext='Los registros de los empleados quedarán libres y volverán a salir en nóminas futuras.' />
				<Table details striped data={[
					['ID', payout.payout_id],
					['Semana', `${moment.unix(payout.date_start).format('DD/MM/YYYY')} - ${moment.unix(payout.date_end).format('DD/MM/YYYY')}`],
					['Empleados', rows.length],
					['Total', '$' + addCommas(total)],
				]} />
				<Message type='error' list={prompts} />
			</Modal.Content>
			<Modal.Actions>
				<Button text='Cerrar' basic onClick={()=> setShownModal(null)} />
				<Button text='Eliminar nómina' color='red' loading={loading} onClick={deletePayout} />
			</Modal.Actions>
		</Modal>
		<Modal open={shownModal === Modals.CANCEL} size='tiny' onClose={()=> setShownModal(null)}>
			<Modal.Header>¿Cancelar pago?</Modal.Header>
			{selectedRow && <Modal.Content>
				<Header text='¿Cancelar el pago de este empleado?' subtext={`${selectedRow.name} — sus días quedan cancelados y no saldrán en nóminas futuras.`} />
				<Table details striped data={[
					['Empleado', selectedRow.name],
					['Pagos', selectedRow.payments],
					['Monto', '$' + addCommas(selectedRow.net || 0)],
				]} />
				<Message type='error' list={prompts} />
			</Modal.Content>}
			<Modal.Actions>
				<Button text='Cerrar' basic onClick={()=> setShownModal(null)} />
				<Button text='Cancelar pago' color='red' loading={loading}
					onClick={()=> runAction(API.cancelEmploymentPayout(payout.payout_id, [selectedRow.user_id]), 'Empleado cancelado!', 'Hubo un error cancelando el empleado (CAN-1)')} />
			</Modal.Actions>
		</Modal>
	</div>
}

export default EmploymentPayoutView;
